import Pako from 'pako'
import { transformApiResponse } from 'utils'

export enum SocketEventName {
  /** 币币行情 */
  MarketM = 'Pushdata.marketM',
  /** 合约行情 */
  DoubleM = 'Pushdata.doubleM',
  /** 币币K线 */
  Kline = 'Pushdata.kline',
  /** 盘口 */
  Handicap = 'Pushdata.depth',
  /** 实时成交 */
  Order = 'Pushdata.orderbook',
}

export class SocketService {
  private socket: WebSocket | null = null
  private openCallback: ((event: Event) => void)[] = []
  private closeCallback: ((event: CloseEvent) => void)[] = []
  private emitTaskQueue: (() => void)[] = []
  public isConnected = false
  private eventMap: Record<string, (<T extends Record<string, any>>(data: T) => void)[]> = {}
  private initPendingTaskQueue: (() => void)[] = []

  // 重连相关配置
  private reconnectAttempts = 0
  private maxReconnectAttempts = 15
  private reconnectInterval = 3000 // 3秒
  private reconnectTimer: NodeJS.Timeout | null = null
  private wsUrl: string = ''
  public isPrintLogger = false

  private heartTimer: NodeJS.Timeout | null = null

  /** 断线重连后的回调队列 */
  private reconnectCallbackQueue: (() => void)[] = []
  private isFromReconnect = false

  constructor() {}

  protected logger(message: string, data?: any) {
    if (!this.isPrintLogger) return
    console.log(`[SocketService] ${message}`, data)
  }

  private handleOpen = (event: Event) => {
    const websocket = event.target as WebSocket

    if (websocket.readyState === WebSocket.OPEN) {
      this.isConnected = true
      this.reconnectAttempts = 0 // 重置重连次数
      // 发送积压的消息
      while (this.emitTaskQueue.length > 0) {
        const task = this.emitTaskQueue.shift()
        task?.()
      }
      this.heartbeat()

      this.openCallback.forEach((callback) => {
        callback(event)
      })
      // 不仅仅是重连，还有：切换语言，WS链接变化，也会useWebSocketStore出触发重新创建， 也需要重新订阅事件
      // if (this.isFromReconnect) {
      this.reconnectCallbackQueue.forEach((callback) => {
        callback()
      })
      this.isFromReconnect = false
      // }
    }
  }

  private handleMessage = async (event: MessageEvent) => {
    const data = await this.judgeWSType(event)
    if (!data) return
    const eventName = (data as Record<string, any>).action as string
    if (this.isPrintLogger) {
      this.logger(eventName, data)
    }
    if (this.eventMap[eventName]) {
      this.eventMap[eventName].forEach((callback) => {
        callback(transformApiResponse(data))
      })
    }
  }

  private handleClose = (event: CloseEvent) => {
    this.closeCallback.forEach((callback) => {
      callback(event)
    })
    this.isConnected = false
    this.handleReconnect()
  }

  private handleError = (event: Event) => {
    console.error('WebSocket error', event)
    this.handleReconnect()
  }

  public listen() {
    this.socket?.addEventListener('open', this.handleOpen)
    this.socket?.addEventListener('message', this.handleMessage)
    this.socket?.addEventListener('close', this.handleClose)
    this.socket?.addEventListener('error', this.handleError)
  }

  private handleReconnect() {
    if (this.heartTimer) {
      clearTimeout(this.heartTimer)
      this.heartTimer = null
    }
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('WebSocket 连接失败，达到最大重试次数')
      return
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++
      // console.log(this.wsUrl, `正在尝试重新连接 (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
      this.isFromReconnect = true
      this.createSocket(this.wsUrl)
    }, this.reconnectInterval)
  }

  /** 解析二进制数据 */
  private async judgeWSType(res: MessageEvent) {
    return new Promise((resolve, reject) => {
      if (typeof res.data == 'string') {
        if (res.data == 'pong') {
          return resolve(null)
        }
        return resolve(JSON.parse(res.data))
      } else {
        const reader = new FileReader() //创建文件类型
        reader.readAsArrayBuffer(res.data) // 启动读取指定的 Blob 读取完成并触发 load 事件 （result是ArrayBuffer类型）
        reader.onload = () => {
          // 用第三方包解压
          const txt = Pako.ungzip(reader.result as ArrayBuffer, { to: 'string' })
          if (txt == 'pong') {
            // 返回错误
            return resolve(null)
          }
          const data = JSON.parse(txt)
          if (data.data.data && data.data.field) {
            //此处针对 ws2 =>ws3 数据格式转化
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const formattedArrayData = (data: any, _action: string) => {
              const arr: Record<string, any>[] = []
              let copyArr: Record<string, any>[] = []
              const title = data.field.split(',')
              data['data'].forEach((item: any, i: number) => {
                const line = item.split(',')
                arr[i] = new Object()
                arr[i]['currency'] = new Object()
                title.forEach((m: any, n: number) => {
                  arr[i][m] = +line[n]
                  arr[i]['currency'][m] = +line[n]
                })
              })
              copyArr = arr
              return copyArr
            }
            const tempData = data
            tempData.data = formattedArrayData(data.data, tempData.action)
            return resolve(tempData)
          }
          resolve(data)
        }
        reader.onerror = (e) => {
          reject(e)
        }
      }
    })
  }

  public createSocket(wsUrl: string) {
    this.wsUrl = wsUrl
    this.socket = new WebSocket(wsUrl)
    this.listen()
    while (this.initPendingTaskQueue.length > 0) {
      const task = this.initPendingTaskQueue.shift()
      task?.()
    }
  }

  public onOpen(callback: (event: Event) => void) {
    const index = this.openCallback.indexOf(callback)
    if (index === -1) {
      this.openCallback.push(callback)
    } else {
      this.openCallback[index] = callback
    }
  }

  /** 监听消息 */
  public onMessage<D extends Record<string, any>>(eventName: SocketEventName, callback: <T extends D>(data: T) => void) {
    if (!this.eventMap[eventName]) {
      this.eventMap[eventName] = []
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const index = this.eventMap[eventName]?.indexOf?.(callback)
    if (index === -1) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      this.eventMap[eventName].push(callback)
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      this.eventMap[eventName][index] = callback
    }
  }

  /** 取消监听消息 */
  public offMessage<D extends Record<string, any>>(eventName: SocketEventName, callback: <T extends D>(data: T) => void) {
    if (!this.eventMap[eventName]) return

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const index = this.eventMap[eventName]?.indexOf?.(callback)
    if (index !== -1) {
      console.log('SocketService.offMessage', eventName, '销毁')
      this.eventMap[eventName].splice(index, 1)
    }
  }

  public onClose(callback: (event: CloseEvent) => void) {
    const index = this.closeCallback.indexOf(callback)
    if (index === -1) {
      this.closeCallback.push(callback)
    } else {
      this.closeCallback[index] = callback
    }
  }

  /** 发送消息 */
  public emit(event: string, data: Record<string, any>) {
    const emitTask = () => {
      const message = {
        action: event,
        data: data,
      }
      const task = () => {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify(message))
        } else {
          // 如果socket已断开，将任务重新放入队列
          console.log('放入队列')
          this.emitTaskQueue.push(task)
        }
      }
      // 先判断ws链接是否正常打开，如果正常打开，则发送消息， 否则将消息存储到队列中，等待连接成功后发送

      if (this.socket?.readyState === WebSocket.OPEN) {
        task()
      } else {
        this.emitTaskQueue.push(task)
      }
    }
    if (!this.socket) {
      this.initPendingTaskQueue.push(emitTask)
    } else {
      emitTask()
    }
  }

  /** 心跳 */
  public heartbeat() {
    this.socket?.send('ping')
    this.heartTimer = setTimeout(() => {
      this.heartbeat()
    }, 5000)
  }

  public unListen() {
    this.socket?.removeEventListener('open', this.handleOpen)
    this.socket?.removeEventListener('message', this.handleMessage)
    this.socket?.removeEventListener('close', this.handleClose)
    this.socket?.removeEventListener('error', this.handleError)
  }

  public closeSocket() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.unListen()
    this.socket?.close()
  }

  public onReconnect(callback: () => void) {
    this.reconnectCallbackQueue.push(callback)
  }

  public offReconnect(callback: () => void) {
    const index = this.reconnectCallbackQueue.indexOf(callback)
    if (index !== -1) {
      this.reconnectCallbackQueue.splice(index, 1)
    }
  }

  public dispose() {
    if (!this.socket) return
    console.log('dispose')
    this.closeSocket()
    this.socket = null
    this.openCallback = []
    this.closeCallback = []
    this.emitTaskQueue = []
  }
}
